import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { IntervalCurves } from './IntervalCurves'
import { SchedulingLab } from './lab/SchedulingLab'
import { motion } from '@/utils/motion'
import { fetchSrsSettings, updateSrsSettings, previewSrsSettings } from '@/api/admin'
import type { SrsSettings, SrsPreview } from '@/types/admin'

function SettingsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <span className="sr-only">Loading scheduling parameters</span>
      <div className="h-11 w-full animate-pulse rounded-full bg-surface-bg" />
      <div className="h-24 w-full animate-pulse rounded-md bg-surface-bg" />
      <div className="aspect-[2/1] w-full animate-pulse rounded-md bg-surface-bg" />
    </div>
  )
}

interface SaveBarProps {
  changes: number
  invalid: boolean
  saving: boolean
  error: string | null
  onSave: () => void
  onDiscard: () => void
}

/** Rises into view once anything differs from the saved settings, and stays until it does not. */
function SaveBar({ changes, invalid, saving, error, onSave, onDiscard }: SaveBarProps) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (ref.current) void motion.rise(ref.current)
  }, [])

  return (
    <div ref={ref} className="sticky bottom-4 z-30 mx-auto flex w-full max-w-2xl flex-wrap items-center justify-between gap-3 rounded-full border border-border-divider bg-card/95 py-2 pl-6 pr-2 shadow-lg backdrop-blur">
      <p aria-live="polite" className="font-serif text-sm text-body">
        {invalid
          ? 'Know well must stay below the mastery threshold.'
          : error ?? `${changes} unsaved ${changes === 1 ? 'change' : 'changes'}. Applies to future reviews only.`}
      </p>
      <div className="flex gap-2">
        <Button variant="quiet" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button onClick={onSave} disabled={saving || invalid}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  )
}

export function SrsTab({ active }: { active: boolean }) {
  const [draft, setDraft] = useState<SrsSettings | null>(null)
  const [persisted, setPersisted] = useState<SrsSettings | null>(null)
  const [preview, setPreview] = useState<SrsPreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!active || draft) return
    fetchSrsSettings()
      .then((res) => {
        setDraft(res.data.data)
        setPersisted(res.data.data)
      })
      .catch(() => setLoadError('Could not load the scheduling parameters. Reload to try again.'))
  }, [active, draft])

  // Debounced so dragging a slider does not fire a request per pixel. A draft the
  // server would reject is not worth a round-trip — the ladder just holds still.
  useEffect(() => {
    if (!draft || draft.know_well_interval >= draft.mastery_threshold) return
    const timer = setTimeout(() => {
      previewSrsSettings(draft)
        .then((res) => setPreview(res.data.data))
        .catch(() => setPreview(null))
    }, 200)
    return () => clearTimeout(timer)
  }, [draft])

  function change(key: keyof SrsSettings, value: number) {
    setSaveError(null)
    setDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  function save() {
    if (!draft) return
    setSaving(true)
    setSaveError(null)
    updateSrsSettings(draft)
      .then((res) => setPersisted(res.data.data))
      .catch(() => setSaveError('Could not save. Know well must stay below the mastery threshold.'))
      .finally(() => setSaving(false))
  }

  if (!active) return null
  if (loadError) return <p className="font-serif text-sm text-body">{loadError}</p>
  if (!draft || !persisted) return <SettingsSkeleton />

  const invalid = draft.know_well_interval >= draft.mastery_threshold
  const changes = (Object.keys(draft) as (keyof SrsSettings)[]).filter((key) => draft[key] !== persisted[key]).length

  return (
    <div className="space-y-20">
      <SchedulingLab draft={draft} saved={persisted} onChange={change} valid={!invalid} />

      <section aria-labelledby="srs-preview">
        <h3 id="srs-preview" className="font-serif text-xl text-heading">
          Rating ladders
        </h3>
        <p className="mt-2 max-w-prose font-serif text-sm leading-relaxed text-muted">
          How a new card&rsquo;s interval grows when rated the same way every time. Each curve
          climbs towards the mastery line; the glowing point is where the card leaves the review
          queue.
        </p>

        <div className="mt-8">
          {preview ? (
            <IntervalCurves
              ladder={preview.ladder}
              masteryThreshold={preview.mastery_threshold}
              knowWellInterval={draft.know_well_interval}
            />
          ) : (
            <p className="flex items-center gap-2 font-serif text-sm text-muted">
              <Spinner />
              Projecting intervals...
            </p>
          )}
        </div>
      </section>

      {(changes > 0 || saveError) && (
        <SaveBar
          changes={changes}
          invalid={invalid}
          saving={saving}
          error={saveError}
          onSave={save}
          onDiscard={() => {
            setDraft(persisted)
            setSaveError(null)
          }}
        />
      )}
    </div>
  )
}
